import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import * as _ from 'underscore';
import { RootStoreState } from 'src/app/root-store';
import { takeUntil } from 'rxjs/operators';

import { ActivatedRoute, Router } from '@angular/router';
import { createCustomerAddressError, createCustomerAddressLoading, createCustomerAddressSuccess, createCustomerCompanyError, createCustomerCompanyLoading, createCustomerCompanySuccess, createCustomerError, createCustomerLoading, createCustomerSuccess, getCreatedCustomerAddress, getCreatedCustomerCompany, getCustomer, getCustomerError, getCustomerLoading } from 'src/app/root-store/kwikspace-app-store/selectors/customer.selector';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ModalComponent } from "src/app/core/components/modal/modal.component";
import { StaticDataService } from 'src/app/services/static-data.service';
import { CreateCustomer, CreateCustomerAddress, CreateCustomerCompany } from 'src/app/root-store/kwikspace-app-store/actions/customer.action';
import { AppRoutes } from 'src/app/shared/endpoints.enum';
import { getProvinces, getProvincesError, getProvincesLoading } from 'src/app/root-store/kwikspace-app-store/selectors/kwikspace.selector';
import { GetProvinces } from 'src/app/root-store/kwikspace-app-store/actions/kwikspace.action';

@Component({
  selector: 'kwikspace-new-customer',
  templateUrl: './new-customer.component.html',
  styleUrls: ['./new-customer.component.scss']
})
export class NewCustomerComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();

  newCustomerLoading$: Observable<any> = this.store.select(createCustomerLoading);
  newCustomerError$: Observable<any> = this.store.select(createCustomerError);

  newCustomerCompanyLoading$: Observable<any> = this.store.select(createCustomerCompanyLoading);
  newCustomerCompanyError$: Observable<any> = this.store.select(createCustomerCompanyError);

  newCustomerAddressLoading$: Observable<any> = this.store.select(createCustomerAddressLoading);
  newCustomerAddressError$: Observable<any> = this.store.select(createCustomerAddressError);

  provincesLoading$: Observable<any> = this.store.select(getProvincesLoading);
  provincesError$: Observable<any> = this.store.select(getProvincesError);

  customerForm: FormGroup;
  companyForm: FormGroup;
  addressForm: FormGroup;

  provinces: any[];
  selectedProvince;

  activeStep: number = 1;
  createdCustomerCompany;
  createdCustomerAddress;

  stepper = [
    {
      id: 1,
      name: "Company Details",
      status: "progress"
    },
    {
      id: 2,
      name: "Address Details",
      status: "todo"
    },
    {
      id: 3,
      name: "Customer Details",
      status: "todo"
    }
  ];

  isMobile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(window.innerWidth <= 768);

  @HostListener('window:resize', ['$event'])
  setIsMobile(event) {
    const isMobile = event.target.innerWidth <= 768;
    this.isMobile.next(isMobile);
  }

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<RootStoreState.RootState>,
    private route: ActivatedRoute,
    private router: Router,
    private staticDataService: StaticDataService,
  ) { }

  ngOnInit() {
    this.customerForm = this.formBuilder.group({
      first_name: [null, [Validators.required, Validators.maxLength(50)]],
      last_name: [null, [Validators.required, Validators.maxLength(50)]],
      email: [null, [Validators.email]]
    });

    this.companyForm = this.formBuilder.group({
      name: [null, [Validators.required, Validators.maxLength(255)]],
      description: [null, []],
      contact_person: [null, [Validators.required, Validators.maxLength(50)]],
      email: [null, [Validators.email]],
      contact_number: [null, [Validators.required, Validators.compose([Validators.pattern('^[0-9]{9,15}$')])]],
      cell_number: [null, Validators.compose([Validators.pattern('^[0-9]{9,15}$')])]
    });

    this.addressForm = this.formBuilder.group({
      address_line1: [null, [Validators.required]],
      address_line2: [null, [Validators.required]],
      suburb: [null, [Validators.required]],
      province: [null, [Validators.required]],
      postcode: [null, [Validators.required]],
    });

    this.store
      .select(getProvinces)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((provinces: any) => {
        if (provinces && provinces.results) {
          this.provinces = provinces.results;
        }
      });

    this.store.dispatch(new GetProvinces());

  }

  setProvince() {
    this.selectedProvince = this.addressForm.value.province
  }

  saveCompany() {
    const payload: any = {
      name: this.companyForm.value.name,
      description: this.companyForm.value.description,
      contact_person: this.companyForm.value.contact_person,
      email: this.companyForm.value.email,
      contact_number: this.companyForm.value.contact_number,
      cell_number: this.companyForm.value.cell_number
    };

    this.store
      .select(createCustomerCompanySuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((company) => {
        if (company) {
          this.activeStep = 2;
          this.stepper[0].status = 'complete';
          this.stepper[1].status = 'progress';
        }
      });

    this.store
      .select(getCreatedCustomerCompany)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((company) => {
        if (company) {
          this.createdCustomerCompany = company;
        }
      });

    this.store.dispatch(new CreateCustomerCompany(payload));
  }

  saveAddress() {
    const payload: any = {
      address_line1: this.addressForm.value.address_line1,
      address_line2: this.addressForm.value.address_line2,
      suburb: this.addressForm.value.suburb,
      province: this.selectedProvince,
      postcode: this.addressForm.value.postcode,
    };

    this.store
      .select(createCustomerAddressSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((company) => {
        if (company) {
          this.activeStep = 3;
          this.stepper[1].status = 'complete';
          this.stepper[2].status = 'progress;'
        }
      });

    this.store
      .select(getCreatedCustomerAddress)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((address) => {
        if (address) {
          this.createdCustomerAddress = address;
        }
      });

    this.store.dispatch(new CreateCustomerAddress(payload));
  }

  saveCustomer() {
    if (this.createdCustomerCompany && this.createdCustomerAddress) {
      const payload: any = {
        first_name: this.customerForm.value.first_name,
        last_name: this.customerForm.value.last_name,
        email: this.customerForm.value.email,
        company: this.createdCustomerCompany.id,
        address: this.createdCustomerAddress.id,
        role: "customer"
      };

      this.store
        .select(createCustomerSuccess)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((customer) => {
          if (customer) {
            const route = AppRoutes.PWA + AppRoutes.CUSTOMER_LIST;
            this.router.navigate([route]);
          }
        });

      this.store.dispatch(new CreateCustomer(payload));
    }

  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
